import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { useTheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import { ValidationMessage } from 'tools/constants/payment';

import { formatCardExpiry } from 'helpers/paymentCard';

import useIxoPayJs from 'hooks/useIxoPayJs';

import type { IxoPayJsStyles } from 'types/ixopay';
import type { IxoPayFieldError, IxoPayTokenizeData } from 'types/ixopay';

// IDs must be unique per mounted instance so multiple modals on the same page don't clash.
let instanceCounter = 0;

const IFRAME_HEIGHT = '40px';
const EXPIRY_CENTURY_PREFIX = '20';

export interface IxoPayCardFormSubmitResult {
  cardHolder: string;
  expiry: string;
  lastFourDigits?: string;
  setAsDefault: boolean;
  token: string;
}

export interface IxoPayCardFormRef {
  submit: () => Promise<IxoPayCardFormSubmitResult>;
}

interface Props {
  onReady?: (ready: boolean) => void;
  publicIntegrationKey: string | null | undefined;
  showSetAsDefault?: boolean;
  /** Full-width name/number fields with an Expiration + CVV row (mobile webview layout). */
  stacked?: boolean;
}

const IxoPayCardForm = forwardRef<IxoPayCardFormRef, Props>(
  (
    { publicIntegrationKey, onReady, showSetAsDefault = true, stacked = false },
    ref,
  ) => {
    const instanceId = useRef<number>((instanceCounter += 1));
    const numberId = `ixopay-cc-number-${instanceId.current}`;
    const cvvId = `ixopay-cc-csc-${instanceId.current}`;

    const { palette } = useTheme();

    const [cardHolder, setCardHolder] = useState('');
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [expiry, setExpiry] = useState('');
    const [setAsDefault, setSetAsDefault] = useState(false);

    // CSS property names are hyphenated because setNumberStyle/setCvvStyle
    // apply them to the iframe element directly (not React inline styles).
    const iframeStyles = useMemo<IxoPayJsStyles>(
      () => ({
        cvv: {
          'background-color': palette.cardKnox.input.background,
          border: `1px solid ${palette.cardKnox.input.border}`,
          'border-radius': '8px',
          'box-sizing': 'border-box',
          color: palette.cardKnox.input.color,
          'font-size': '14px',
          'font-weight': '500',
          height: '40px',
          outline: 'none',
          padding: '10px 16px',
          'placeholder-color': palette.text.disabled,
          width: '100%',
        },
        number: {
          'background-color': palette.cardKnox.input.background,
          border: `1px solid ${palette.cardKnox.input.border}`,
          'border-radius': '8px',
          'box-sizing': 'border-box',
          color: palette.cardKnox.input.color,
          'font-size': '14px',
          'font-weight': '500',
          height: '40px',
          outline: 'none',
          padding: '10px 16px',
          'placeholder-color': palette.text.disabled,
          width: '100%',
        },
      }),
      [
        palette.cardKnox.input.background,
        palette.cardKnox.input.border,
        palette.cardKnox.input.color,
        palette.text.disabled,
      ],
    );

    const { isReady, tokenize, validity } = useIxoPayJs(
      publicIntegrationKey,
      numberId,
      cvvId,
      iframeStyles,
    );

    useEffect(() => {
      onReady?.(isReady);
    }, [isReady, onReady]);

    useEffect(() => {
      if (!isReady) return;
      const observers: MutationObserver[] = [];
      [numberId, cvvId].forEach((divId) => {
        const el = document.getElementById(divId);
        if (!el) return;
        const forceHeight = () => {
          const iframe = el.querySelector<HTMLIFrameElement>('iframe');
          if (iframe && iframe.style.height !== IFRAME_HEIGHT) {
            iframe.style.height = IFRAME_HEIGHT;
          }
        };
        forceHeight();
        const observer = new MutationObserver(forceHeight);
        observer.observe(el, {
          attributeFilter: ['style'],
          attributes: true,
          subtree: true,
        });
        observers.push(observer);
      });
      return () => observers.forEach((o) => o.disconnect());
    }, [isReady, numberId, cvvId]);

    // Clear iframe field errors as soon as the SDK reports them valid
    useEffect(() => {
      setErrors((prev) => {
        const next = { ...prev };
        if (validity.validNumber) delete next.cardNumber;
        if (validity.validCvv) delete next.cvv;
        return next;
      });
    }, [validity.validNumber, validity.validCvv]);

    const validate = useCallback((): boolean => {
      const nextErrors: Record<string, string> = {};
      if (!cardHolder.trim())
        nextErrors.cardHolder = ValidationMessage.CARD_HOLDER_REQUIRED;
      const [month = '', yr = ''] = expiry.split('/');
      if (
        !month ||
        !/^\d{1,2}$/.test(month) ||
        +month < 1 ||
        +month > 12 ||
        !yr ||
        !/^\d{2,4}$/.test(yr)
      ) {
        nextErrors.expiry = ValidationMessage.CARD_EXPIRY_INVALID;
      }
      if (!validity.validNumber)
        nextErrors.cardNumber = ValidationMessage.CARD_NUMBER_INVALID;
      if (!validity.validCvv)
        nextErrors.cvv = ValidationMessage.CARD_CVV_INVALID;
      setErrors(nextErrors);
      return Object.keys(nextErrors).length === 0;
    }, [cardHolder, expiry, validity]);

    useImperativeHandle(
      ref,
      () => ({
        submit: async (): Promise<IxoPayCardFormSubmitResult> => {
          if (!isReady) throw new Error(ValidationMessage.CARD_FORM_NOT_READY);
          if (!validate())
            throw new Error(ValidationMessage.CARD_FIELDS_INVALID);

          const [month = '', yr = ''] = expiry.split('/');
          const year = yr.length === 2 ? `${EXPIRY_CENTURY_PREFIX}${yr}` : yr;

          const tokenizeData: IxoPayTokenizeData = {
            card_holder: cardHolder.trim(),
            month: month.padStart(2, '0'),
            year,
          };

          return tokenize(tokenizeData)
            .then(({ cardData, token }) => ({
              cardHolder: cardHolder.trim(),
              expiry: `${tokenizeData.month}${year.slice(-2)}`,
              lastFourDigits: cardData.last_four_digits,
              setAsDefault,
              token,
            }))
            .catch((fieldErrors: IxoPayFieldError[]) => {
              const next: Record<string, string> = {};
              fieldErrors.forEach(({ attribute, message }) => {
                next[attribute] = message;
              });
              setErrors(next);
              throw new Error(ValidationMessage.CARD_DETAILS_INVALID);
            });
        },
      }),
      [isReady, validate, cardHolder, expiry, tokenize, setAsDefault],
    );

    const nameField = (
      <Box sx={{ width: '100%' }}>
        <Typography fontWeight={600} pb={0.5} variant="subtitle2">
          {stacked ? 'Name On Card' : 'Cardholder Name'} *
        </Typography>
        <TextField
          error={Boolean(errors.cardHolder)}
          fullWidth
          helperText={errors.cardHolder}
          onChange={(e) => {
            setCardHolder(e.target.value);
            if (errors.cardHolder)
              setErrors((prev) => ({ ...prev, cardHolder: '' }));
          }}
          placeholder={stacked ? 'Name On Card' : 'Full name on card'}
          sx={{
            '& input::placeholder': { color: 'text.disabled', opacity: 1 },
          }}
          value={cardHolder}
          variant="outlined"
        />
      </Box>
    );

    const numberField = (
      <Box
        sx={
          stacked
            ? { width: '100%' }
            : { minWidth: 0, width: 'calc(50% - 16px)' }
        }
      >
        <Typography fontWeight={600} pb={0.5} variant="subtitle2">
          Card Number *
        </Typography>
        <Box id={numberId} sx={{ lineHeight: 0, minHeight: IFRAME_HEIGHT }} />
        {errors.cardNumber && (
          <Typography
            color="error"
            sx={{ fontSize: '11px', mt: 0.5 }}
            variant="caption"
          >
            {errors.cardNumber}
          </Typography>
        )}
      </Box>
    );

    const expiryField = (
      <Box
        sx={
          stacked
            ? { minWidth: 0, width: 'calc(50% - 8px)' }
            : { minWidth: 0, width: 'calc(30% - 10px)' }
        }
      >
        <Typography fontWeight={600} pb={0.5} variant="subtitle2">
          {stacked ? 'Expiration' : 'Exp. Date'} *
        </Typography>
        <TextField
          error={Boolean(errors.expiry)}
          fullWidth
          helperText={errors.expiry}
          inputProps={{ maxLength: 5 }}
          InputProps={{ onKeyUp: formatCardExpiry }}
          onChange={(e) => {
            setExpiry(e.target.value);
            if (errors.expiry) setErrors((prev) => ({ ...prev, expiry: '' }));
          }}
          placeholder="MM/YY"
          sx={{
            '& input::placeholder': { color: 'text.disabled', opacity: 1 },
          }}
          value={expiry}
          variant="outlined"
        />
      </Box>
    );

    const cvvField = (
      <Box
        sx={
          stacked
            ? { minWidth: 0, width: 'calc(50% - 8px)' }
            : { minWidth: 0, width: 'calc(20% - 6px)' }
        }
      >
        <Typography fontWeight={600} pb={0.5} variant="subtitle2">
          CVV *
        </Typography>
        <Box id={cvvId} sx={{ lineHeight: 0, minHeight: IFRAME_HEIGHT }} />
        {errors.cvv && (
          <Typography
            color="error"
            sx={{ fontSize: '11px', mt: 0.5 }}
            variant="caption"
          >
            {errors.cvv}
          </Typography>
        )}
      </Box>
    );

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {nameField}

        {stacked ? (
          <>
            {numberField}
            <Box alignItems="flex-start" columnGap={2} display="flex">
              {expiryField}
              {cvvField}
            </Box>
          </>
        ) : (
          <Box alignItems="flex-start" columnGap={2} display="flex">
            {numberField}
            {expiryField}
            {cvvField}
          </Box>
        )}

        {showSetAsDefault && (
          <Box
            sx={{
              border: '1px solid',
              borderColor: ({ palette }) => palette.divider,
              borderRadius: '8px',
              mt: 1,
              px: 2,
              py: 1.5,
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={setAsDefault}
                  onChange={(e) => setSetAsDefault(e.target.checked)}
                  sx={{ mr: 1 }}
                />
              }
              label={
                <Box>
                  <Typography fontWeight={500} variant="body2">
                    Save as default payment method
                  </Typography>
                  <Typography color="text.secondary" variant="caption">
                    Store this card on file for future payments
                  </Typography>
                </Box>
              }
              sx={{ alignItems: 'center', m: 0 }}
            />
          </Box>
        )}
      </Box>
    );
  },
);

IxoPayCardForm.displayName = 'IxoPayCardForm';

export default IxoPayCardForm;
